import React from 'react';

import { uniqBy } from 'es-toolkit';
import { isEmpty } from 'es-toolkit/compat';

import {
  InboxItemsScrollableListQueryVariables,
  InboxListItemInfoFragment,
  useInboxItemsScrollableListQuery,
} from 'src/api';
import { RESOURCES_INBOX_ITEM } from 'src/constants/resources';
import { InboxItemExtended } from 'src/types/inbox';

import { useResourcesInboxItem } from '../resources/useResourcesInboxItem';

import { useInboxItemsQueryVariables } from './useInboxItemsQueryVariables';

type InboxItemsHookResult = {
  inboxItems: InboxItemExtended<InboxListItemInfoFragment>[];
  inboxItemsCount: number;
  onLoadMore: (lastItemId?: string) => Promise<void>;
  loading: boolean;
  isEmptyInbox: boolean;
  hasReachedEnd: boolean;
};

export const useInboxItems = (): InboxItemsHookResult => {
  const variables = useInboxItemsQueryVariables();

  const {
    data: inboxItemsListQuery,
    fetchMore,
    loading,
  } = useInboxItemsScrollableListQuery({ variables });

  const { hasResourcesInboxItem, loading: resourcesInboxItemLoading } = useResourcesInboxItem();

  const inboxItemsCount = React.useMemo(() => {
    return inboxItemsListQuery?.getInboxListItemsCount || 0;
  }, [inboxItemsListQuery?.getInboxListItemsCount]);

  const inboxItems = React.useMemo(() => {
    const inboxItemsRaw = inboxItemsListQuery?.getInboxItemsList ?? [];
    const inboxItemsTyped = inboxItemsRaw as InboxItemExtended<InboxListItemInfoFragment>[];
    const inboxItemsSorted = [...inboxItemsTyped].sort(
      (a, b) => +new Date(b.updatedAt) - +new Date(a.updatedAt),
    );

    return inboxItemsSorted;
  }, [inboxItemsListQuery?.getInboxItemsList]);

  const isEmptyInbox = React.useMemo(() => inboxItemsCount === 0, [inboxItemsCount]);

  const hasReachedEnd = React.useMemo(
    () => inboxItems.length === inboxItemsCount,
    [inboxItems.length, inboxItemsCount],
  );

  const hasNoFilters = React.useMemo(() => isEmpty(variables.where ?? {}), [variables.where]);

  const shouldAddResourcesInboxItem = React.useMemo(
    () => hasResourcesInboxItem && !loading && isEmptyInbox && hasNoFilters,
    [hasResourcesInboxItem, loading, isEmptyInbox, hasNoFilters],
  );

  const onLoadMore = React.useCallback(
    async (lastItemId?: string) => {
      if (!lastItemId) return;
      await fetchMore({
        variables: {
          where: variables.where,
          skip: 1,
          cursorId: lastItemId,
        } satisfies InboxItemsScrollableListQueryVariables,
        updateQuery: (previousResult, options) => {
          const prevItems = previousResult.getInboxItemsList || [];
          const nextItems = options.fetchMoreResult.getInboxItemsList || [];
          const updatedList = uniqBy([...prevItems, ...nextItems], item => item.id);
          return {
            ...previousResult,
            getInboxItemsList: updatedList,
          };
        },
      });
    },
    [fetchMore, variables],
  );

  if (shouldAddResourcesInboxItem) {
    return {
      inboxItems: [RESOURCES_INBOX_ITEM],
      inboxItemsCount: 1,
      onLoadMore,
      loading: loading && resourcesInboxItemLoading,
      isEmptyInbox: false,
      hasReachedEnd,
    };
  }

  return {
    inboxItems,
    inboxItemsCount,
    onLoadMore,
    loading,
    isEmptyInbox,
    hasReachedEnd,
  };
};
